/**
 * Company Service
 *
 * Provides read/write access to the current company's invoicing details
 * (name, address, logo, VAT number, registration number, bank details, contact info).
 *
 * All functions call the backend API endpoints via /api/companies/{companyId}/details.
 * Returns default values when no companyId is available (e.g. demo mode).
 */

import type { Company, BankDetails } from '@/types/company';
import type { Address } from '@/types/index';
import { getApiBaseUrl } from '@/lib/data-service';
import { authFetch } from '@/lib/auth-fetch';

// ============================================================================
// TYPES
// ============================================================================

/**
 * The subset of Company fields that are managed by the Company Details settings page.
 * These are the fields that appear on invoices and can be edited by the user.
 */
export interface CompanyDetails {
  name: string;
  companyRegistrationNumber?: string;
  vatNumber?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
  registeredAddress?: Address;
  bankDetails?: BankDetails;
  // Field length constraints (Settings > General)
  projectCodeMaxLength: number;
  projectNameMaxLength: number;
  descriptionMaxLength: number;
}

/**
 * A cash book (bank) account available in the connected finance system.
 * Returned fully formatted by the backend — the `label` is built server side.
 */
export interface CashBookAccountOption {
  accountKey?: number | null;
  accountCode?: string | null;
  accountName?: string | null;
  label: string;
  bankName: string;
  bankAccountName: string;
  sortCode: string;
  accountNumber: string;
  iban?: string | null;
  swiftBic?: string | null;
}

export interface CashBookAccountsResponse {
  accounts: CashBookAccountOption[];
  selectedAccountKey?: number | null;
  selectedAccountCode?: string | null;
  errorMessage?: string | null;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_COMPANY_DETAILS: CompanyDetails = {
  name: '',
  companyRegistrationNumber: '',
  vatNumber: '',
  phone: '',
  email: '',
  website: '',
  logoUrl: undefined,
  registeredAddress: {
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
  },
  bankDetails: {
    bankName: '',
    accountName: '',
    sortCode: '',
    accountNumber: '',
    iban: '',
    swiftBic: '',
    cashBookAccountKey: null,
    cashBookAccountCode: null,
  },
  projectCodeMaxLength: 10,
  projectNameMaxLength: 80,
  descriptionMaxLength: 80,
};

// ============================================================================
// HELPERS — map flat DB columns to nested frontend shape and back
// ============================================================================

function mapApiResponseToDetails(data: Record<string, unknown>): CompanyDetails {
  return {
    name: (data.name as string) ?? '',
    companyRegistrationNumber: data.companyRegistrationNumber as string | undefined,
    vatNumber: data.vatNumber as string | undefined,
    phone: data.phone as string | undefined,
    email: data.email as string | undefined,
    website: data.website as string | undefined,
    logoUrl: data.logoUrl as string | undefined,
    registeredAddress: {
      line1: (data.registeredAddressLine1 as string) ?? '',
      line2: (data.registeredAddressLine2 as string) ?? '',
      city: (data.registeredAddressCity as string) ?? '',
      county: (data.registeredAddressCounty as string) ?? '',
      postcode: (data.registeredAddressPostcode as string) ?? '',
      country: (data.registeredAddressCountry as string) ?? 'United Kingdom',
    },
    bankDetails: {
      bankName: (data.bankName as string) ?? '',
      accountName: (data.bankAccountName as string) ?? '',
      sortCode: (data.bankSortCode as string) ?? '',
      accountNumber: (data.bankAccountNumber as string) ?? '',
      iban: (data.bankIban as string) ?? '',
      swiftBic: (data.bankSwiftBic as string) ?? '',
      cashBookAccountKey: (data.bankCashBookAccountKey as number | null) ?? null,
      cashBookAccountCode: (data.bankCashBookAccountCode as string | null) ?? null,
    },
    projectCodeMaxLength: (data.projectCodeMaxLength as number) ?? 10,
    projectNameMaxLength: (data.projectNameMaxLength as number) ?? 80,
    descriptionMaxLength: (data.descriptionMaxLength as number) ?? 80,
  };
}

function mapDetailsToApiPayload(details: CompanyDetails): Record<string, unknown> {
  return {
    companyRegistrationNumber: details.companyRegistrationNumber ?? null,
    vatNumber: details.vatNumber ?? null,
    phone: details.phone ?? null,
    email: details.email ?? null,
    website: details.website ?? null,
    logoUrl: details.logoUrl ?? null,
    registeredAddressLine1: details.registeredAddress?.line1 ?? null,
    registeredAddressLine2: details.registeredAddress?.line2 ?? null,
    registeredAddressCity: details.registeredAddress?.city ?? null,
    registeredAddressCounty: details.registeredAddress?.county ?? null,
    registeredAddressPostcode: details.registeredAddress?.postcode ?? null,
    registeredAddressCountry: details.registeredAddress?.country ?? null,
    bankName: details.bankDetails?.bankName ?? null,
    bankAccountName: details.bankDetails?.accountName ?? null,
    bankSortCode: details.bankDetails?.sortCode ?? null,
    bankAccountNumber: details.bankDetails?.accountNumber ?? null,
    bankIban: details.bankDetails?.iban ?? null,
    bankSwiftBic: details.bankDetails?.swiftBic ?? null,
    bankCashBookAccountKey: details.bankDetails?.cashBookAccountKey ?? null,
    bankCashBookAccountCode: details.bankDetails?.cashBookAccountCode ?? null,
    projectCodeMaxLength: details.projectCodeMaxLength,
    projectNameMaxLength: details.projectNameMaxLength,
    descriptionMaxLength: details.descriptionMaxLength,
  };
}


// ============================================================================
// COMPANY ID RESOLUTION
// ============================================================================

let _companyId: number | null = null;

/**
 * Set the current company ID so that subsequent service calls target the
 * correct backend resource. Called from CompanyContext or app bootstrap.
 */
export function setCurrentCompanyId(id: number | null): void {
  _companyId = id;
}

function getBaseUrl(): string | null {
  return _companyId ? `${getApiBaseUrl()}/companies/${_companyId}` : null;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Returns the current company's invoicing details.
 *
 * API: GET /api/companies/{id}/details
 */
export async function getCompanyDetails(): Promise<CompanyDetails> {
  const base = getBaseUrl();
  if (!base) return { ...DEFAULT_COMPANY_DETAILS };

  try {
    const response = await authFetch(`${base}/details`);
    if (!response.ok) {
      console.warn(`Failed to fetch company details (${response.status}), using defaults`);
      return { ...DEFAULT_COMPANY_DETAILS };
    }
    const data = await response.json();
    return mapApiResponseToDetails(data);
  } catch (error) {
    console.warn('Failed to fetch company details, using defaults', error);
    return { ...DEFAULT_COMPANY_DETAILS };
  }
}

// ============================================================================
// WRITE
// ============================================================================

/**
 * Saves updated company details.
 *
 * API: PUT /api/companies/{id}/details
 */
export async function updateCompanyDetails(
  updates: Partial<CompanyDetails>,
): Promise<CompanyDetails> {
  const current = await getCompanyDetails();
  const merged: CompanyDetails = {
    ...current,
    ...updates,
    registeredAddress:
      updates.registeredAddress !== undefined
        ? { ...current.registeredAddress, ...updates.registeredAddress }
        : current.registeredAddress,
    bankDetails:
      updates.bankDetails !== undefined
        ? ({ ...current.bankDetails, ...updates.bankDetails } as BankDetails)
        : current.bankDetails,
  };

  const base = getBaseUrl();
  if (!base) {
    throw new Error('Cannot update company details: no company ID set');
  }

  const response = await authFetch(`${base}/details`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapDetailsToApiPayload(merged)),
  });
  if (!response.ok) {
    throw new Error(`Failed to update company details: ${response.statusText}`);
  }
  return merged;
}

/**
 * Returns the cash book (bank) accounts available in the connected finance
 * integration, together with the account currently selected for the company.
 *
 * API: GET /api/integrations/finance/cash-book-accounts
 */
export async function getCashBookAccounts(): Promise<CashBookAccountsResponse> {
  const response = await authFetch(`${getApiBaseUrl()}/integrations/finance/cash-book-accounts`);
  if (!response.ok) {
    throw new Error(`Failed to fetch bank accounts: ${response.statusText}`);
  }
  return (await response.json()) as CashBookAccountsResponse;
}

/**
 * Uploads a company logo.
 *
 * API: POST /api/companies/{id}/logo
 */
export async function uploadCompanyLogo(base64DataUrl: string): Promise<string> {
  const base = getBaseUrl();
  if (!base) {
    throw new Error('Cannot upload logo: no company ID set');
  }

  const response = await authFetch(`${base}/logo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logoUrl: base64DataUrl }),
  });
  if (!response.ok) {
    throw new Error(`Failed to upload company logo: ${response.statusText}`);
  }
  const data = await response.json();
  return data.logoUrl;
}

/**
 * Removes the company logo.
 *
 * API: DELETE /api/companies/{id}/logo
 */
export async function removeCompanyLogo(): Promise<void> {
  const base = getBaseUrl();
  if (!base) {
    throw new Error('Cannot remove logo: no company ID set');
  }

  const response = await authFetch(`${base}/logo`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to remove company logo: ${response.statusText}`);
  }
}

/**
 * Maps a CompanyDetails object to the fields expected by the Company type
 * used throughout the application. Useful when merging saved details back
 * into CompanyContext.
 */
export function mapToCompanyFields(details: CompanyDetails): Partial<Company> {
  return {
    name: details.name,
    companyRegistrationNumber: details.companyRegistrationNumber,
    vatNumber: details.vatNumber,
    phone: details.phone,
    email: details.email,
    website: details.website,
    logoUrl: details.logoUrl,
    registeredAddress: details.registeredAddress,
    bankDetails: details.bankDetails,
  };
}
